import { Alert, Button, Modal, todayInArgentina } from '@catering/ui';
import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Order } from './api';
import { ConfirmationModal } from './confirmation-modal';
import { describeOrderError } from './error-messages';
import { MenuSelector } from './menu-selector';
import { useMenuCalendar } from './use-menu-calendar';
import { useCreateOrder, useRepeatOrder, useSubmitOrder, useUpdateOrder } from './use-order';

export interface OrderFormSite {
  id: string;
  name: string;
}

export interface OrderFormOperatingUnit {
  id: string;
  name: string;
}

export interface OrderFormProps {
  sites: OrderFormSite[];
  /** The selector shows only with more than one unit; with one (or none) the form behaves as before. */
  operatingUnits?: OrderFormOperatingUnit[];
  /** Present when editing an existing (pre-cutoff) order; absent for a new one. */
  order?: Order | undefined;
  onSubmitted: (order: Order) => void;
}

function tomorrowIso(): string {
  const date = new Date(`${todayInArgentina()}T00:00:00.000Z`);

  date.setUTCDate(date.getUTCDate() + 1);

  return date.toISOString().slice(0, 10);
}

/**
 * `portal-orders/spec.md` → RF-06-02, 04, 08, 09, 10, 11: site + date +
 * quantities by menu, item and order notes, "Repetir pedido anterior",
 * explicit confirmation before sending, full keyboard operation. Server
 * validation is the only authority (`design.md`); this form never
 * duplicates cutoff/quota/tope logic, it only shows whatever error the
 * server answers via `describeOrderError`.
 */
export function OrderForm({
  sites,
  operatingUnits = [],
  order,
  onSubmitted,
}: OrderFormProps): JSX.Element {
  const [unsentOrder, setUnsentOrder] = useState<Order | null>(null);
  // Saved but not sent (the submit call failed): from then on the form edits and retries that order instead of creating another.
  const target = order ?? unsentOrder ?? undefined;
  const isEdit = target !== undefined;
  const hasUnitChoice = !isEdit && operatingUnits.length > 1;
  const [operatingUnitId, setOperatingUnitId] = useState('');
  const [pendingUnitId, setPendingUnitId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState(order?.siteId ?? sites[0]?.id ?? '');
  const [serviceDate, setServiceDate] = useState(order?.serviceDate ?? tomorrowIso());
  // Legacy items frozen before `rotating-menu` have `positionId === null`
  // (`design.md` §1.4, T6) and are not addressable by any position row,
  // same choice `apps/web-admin`'s `order-form-schema.ts` documents.
  const positionedItems = (order?.items ?? []).filter(
    (item): item is typeof item & { positionId: string } => item.positionId !== null,
  );
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    Object.fromEntries(positionedItems.map((item) => [item.positionId, item.quantity])),
  );
  const [itemNotes, setItemNotes] = useState<Record<string, string>>(() =>
    Object.fromEntries(positionedItems.map((item) => [item.positionId, item.notes ?? ''])),
  );
  const [clientNotes, setClientNotes] = useState(order?.clientNotes ?? '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [repeatUnavailable, setRepeatUnavailable] = useState(false);
  const [repeatDroppedCount, setRepeatDroppedCount] = useState(0);
  // Menus already loaded once (from an order being edited) but no longer in
  // `menuCalendar.data` (a past date, or unpublished since) still need a
  // name for the confirmation summary and the line itself, keyed by
  // `positionId` (`rotating-menu/design.md` §5). Never updated after mount
  // — `order` is a stable prop for the lifetime of this form.
  const knownMenuNames: Record<string, string> = Object.fromEntries(
    positionedItems.map((item) => [item.positionId, item.menuNameSnapshot]),
  );

  // Several units and none chosen yet: no menus to show until the customer picks one.
  const unitParam = hasUnitChoice ? operatingUnitId : undefined;
  const awaitingUnit = hasUnitChoice && operatingUnitId === '';
  const menuCalendar = useMenuCalendar(serviceDate, unitParam || undefined, !awaitingUnit);
  const createOrder = useCreateOrder();
  const updateOrder = useUpdateOrder(target?.id ?? '');
  const submitOrder = useSubmitOrder();
  const repeat = useRepeatOrder();

  const menus = menuCalendar.data ?? [];
  const menuName = (positionId: string): string =>
    menus.find((menu) => menu.positionId === positionId)?.name ??
    knownMenuNames[positionId] ??
    positionId;

  function applyUnit(unitId: string): void {
    setOperatingUnitId(unitId);
    setQuantities({});
    setItemNotes({});
    setRepeatUnavailable(false);
    setRepeatDroppedCount(0);
  }

  /** Menus and prices belong to the unit's contract, so lines loaded for another unit cannot be kept. */
  function handleUnitChange(unitId: string): void {
    const hasLines = Object.values(quantities).some((quantity) => quantity > 0);

    if (hasLines && operatingUnitId !== '') {
      setPendingUnitId(unitId);

      return;
    }

    applyUnit(unitId);
  }

  function handleQuantityChange(positionId: string, quantity: number): void {
    setQuantities((current) => ({ ...current, [positionId]: quantity }));
  }

  function handleNotesChange(positionId: string, notes: string): void {
    setItemNotes((current) => ({ ...current, [positionId]: notes }));
  }

  /**
   * `portal-orders/spec.md` → "Repetir pedido precarga las posiciones con
   * el plato de hoy": resolves each previous line's `positionId` against
   * today's `menuCalendar` instead of copying the previous `menuId`
   * directly. A line with no `positionId` (legacy item) or whose position
   * has no published dish for `serviceDate` is dropped, same criterion
   * `resolveAvailableLines` applies server-side for templates.
   */
  async function handleRepeat(): Promise<void> {
    setSubmitError(null);
    setRepeatDroppedCount(0);

    const result = await repeat.mutateAsync({
      siteId,
      serviceDate,
      ...(unitParam ? { operatingUnitId: unitParam } : {}),
    });

    if (result.data === null) {
      setRepeatUnavailable(true);

      return;
    }

    // The menu of the new date may still be loading (the user picked a date
    // and clicked right away): wait for it instead of treating every line
    // as "no dish published". `cancelRefetch: false` joins the in-flight fetch.
    const dayMenus =
      menuCalendar.data ?? (await menuCalendar.refetch({ cancelRefetch: false })).data ?? [];
    const availablePositionIds = new Set(dayMenus.map((menu) => menu.positionId));
    const eligibleItems = result.data.items.filter(
      (item): item is typeof item & { positionId: string } =>
        item.positionId !== null && availablePositionIds.has(item.positionId),
    );

    setQuantities(
      Object.fromEntries(eligibleItems.map((item) => [item.positionId, item.quantity])),
    );
    setItemNotes(
      Object.fromEntries(eligibleItems.map((item) => [item.positionId, item.notes ?? ''])),
    );
    setRepeatDroppedCount(result.data.items.length - eligibleItems.length);
  }

  function handleReview(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setSubmitError(null);
    setConfirmOpen(true);
  }

  const lines = Object.entries(quantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([positionId, quantity]) => ({ positionId, menuName: menuName(positionId), quantity }));
  const totalUnits = lines.reduce((sum, line) => sum + line.quantity, 0);

  async function handleConfirm(): Promise<void> {
    const items = lines.map(({ positionId, quantity }) => ({
      positionId,
      quantity,
      notes: itemNotes[positionId] || null,
    }));

    try {
      const saved = isEdit
        ? await updateOrder.mutateAsync({ items, clientNotes: clientNotes || null })
        : await createOrder.mutateAsync({
            siteId,
            ...(unitParam ? { operatingUnitId: unitParam } : {}),
            serviceDate,
            items,
            clientNotes: clientNotes || null,
          });

      await send(saved);
    } catch (error) {
      setConfirmOpen(false);
      setSubmitError(describeOrderError(error));
    }
  }

  /** Confirming means sending (`portal-orders/spec.md`): a saved DRAFT is submitted, an already SUBMITTED order being edited stays as the PATCH left it. */
  async function send(saved: Order): Promise<void> {
    if (saved.status !== 'DRAFT') {
      setConfirmOpen(false);
      onSubmitted(saved);

      return;
    }

    try {
      const sent = await submitOrder.mutateAsync(saved.id);

      setUnsentOrder(null);
      setConfirmOpen(false);
      onSubmitted(sent);
    } catch (error) {
      setUnsentOrder(saved);
      setConfirmOpen(false);
      setSubmitError(describeOrderError(error));
    }
  }

  async function handleRetrySend(): Promise<void> {
    if (unsentOrder === null) return;

    setSubmitError(null);
    await send(unsentOrder);
  }

  const submitting =
    (isEdit ? updateOrder.isPending : createOrder.isPending) || submitOrder.isPending;

  return (
    <>
      <form onSubmit={handleReview} className="flex flex-col gap-5">
        {submitError !== null ? (
          <Alert tone="danger">
            <div className="flex flex-col gap-2">
              <span>{submitError}</span>
              {unsentOrder !== null ? (
                <>
                  <span>
                    El pedido {unsentOrder.orderNumber} quedó guardado pero todavía no fue enviado.
                  </span>
                  <div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={submitOrder.isPending}
                      onClick={() => void handleRetrySend()}
                    >
                      Enviar pedido
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          </Alert>
        ) : null}

        {hasUnitChoice ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="operatingUnit" className="text-sm font-medium text-slate-900">
              Punto de venta
            </label>
            <select
              id="operatingUnit"
              value={operatingUnitId}
              onChange={(event) => handleUnitChange(event.target.value)}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
            >
              <option value="" disabled>
                Elegí un punto de venta
              </option>
              {operatingUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {!isEdit ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="site" className="text-sm font-medium text-slate-900">
              Sede
            </label>
            <select
              id="site"
              value={siteId}
              onChange={(event) => setSiteId(event.target.value)}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
            >
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {!isEdit ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="serviceDate" className="text-sm font-medium text-slate-900">
              Fecha de servicio
            </label>
            <input
              id="serviceDate"
              type="date"
              value={serviceDate}
              onChange={(event) => setServiceDate(event.target.value)}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
            />
          </div>
        ) : null}

        {!isEdit ? (
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handleRepeat()}
              loading={repeat.isPending}
              disabled={repeatUnavailable || awaitingUnit}
              title={repeatUnavailable ? 'No hay pedido anterior para repetir' : undefined}
            >
              Repetir pedido anterior
            </Button>
            {repeatDroppedCount > 0 ? (
              <Alert tone="info">
                Se descart{repeatDroppedCount === 1 ? 'ó' : 'aron'} {repeatDroppedCount}{' '}
                {repeatDroppedCount === 1 ? 'línea' : 'líneas'} sin plato publicado hoy.
              </Alert>
            ) : null}
          </div>
        ) : null}

        {awaitingUnit ? (
          <p className="text-sm text-slate-500">Elegí un punto de venta para ver los menús.</p>
        ) : menuCalendar.isPending ? (
          <p className="text-sm text-slate-500">Cargando menús disponibles…</p>
        ) : menus.length === 0 ? (
          <p className="text-sm text-slate-500">No hay menús disponibles para esa fecha.</p>
        ) : (
          <MenuSelector
            menus={menus}
            quantities={quantities}
            onQuantityChange={handleQuantityChange}
            notes={itemNotes}
            onNotesChange={handleNotesChange}
          />
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="clientNotes" className="text-sm font-medium text-slate-900">
            Observaciones del pedido
          </label>
          <textarea
            id="clientNotes"
            value={clientNotes}
            onChange={(event) => setClientNotes(event.target.value)}
            rows={3}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
          />
        </div>

        {/* On phones the portal's fixed bottom tab bar (44 px + border) sits at the
            viewport bottom, so the footer sticks right above it instead. */}
        <div className="sticky bottom-12 z-10 -mx-4 flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3 sm:bottom-0 sm:-mx-0 sm:rounded-md sm:border">
          <span className="text-sm font-medium text-slate-900">
            {totalUnits} {totalUnits === 1 ? 'unidad' : 'unidades'}
          </span>
          <Button type="submit" disabled={lines.length === 0}>
            Revisar pedido
          </Button>
        </div>
      </form>

      <Modal
        open={pendingUnitId !== null}
        onClose={() => setPendingUnitId(null)}
        title="Cambiar de punto de venta"
      >
        <p className="text-sm text-slate-700">
          Los menús son distintos en cada punto de venta. Se van a quitar las líneas cargadas.
        </p>
        <div className="mt-6 flex gap-3">
          <Button variant="secondary" onClick={() => setPendingUnitId(null)} className="flex-1">
            Seguir en este punto
          </Button>
          <Button
            onClick={() => {
              if (pendingUnitId !== null) applyUnit(pendingUnitId);
              setPendingUnitId(null);
            }}
            className="flex-1"
          >
            Cambiar de punto
          </Button>
        </div>
      </Modal>

      <ConfirmationModal
        open={confirmOpen}
        lines={lines}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleConfirm()}
        confirming={submitting}
      />
    </>
  );
}
